import type { NoteDto } from '@keep/shared';
import { UserPlus, X } from 'lucide-react';
import { useState } from 'react';
import { useCurrentUser } from '../../api/auth';
import { useAddCollaborator, useNote, useRemoveCollaborator } from '../../api/notes';
import { Dialog } from '../../components/ui/dialog';
import { IconButton } from '../../components/ui/icon-button';
import { errorMessage } from '../../lib/api';

function Avatar({ name }: { name: string }) {
  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#7b1fa2] text-sm font-medium text-white">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

/** Partage d'une note : la personne invitée doit avoir un compte sur cette instance. */
export function CollaboratorsDialog({
  note: initial,
  onClose,
}: {
  note: NoteDto;
  onClose: () => void;
}) {
  const me = useCurrentUser();
  const { data: note = initial } = useNote(initial.id);
  const add = useAddCollaborator(note.id);
  const remove = useRemoveCollaborator(note.id);
  const [email, setEmail] = useState('');
  const isOwner = note.owner.id === me.id;

  const submit = () => {
    const value = email.trim();
    if (!value) return;
    add.mutate(value, { onSuccess: () => setEmail('') });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Collaborateurs"
      className="max-w-[600px]"
    >
      <div className="flex flex-col gap-3 border-y border-line px-6 py-4 text-sm">
        <div className="flex items-center gap-3">
          <Avatar name={note.owner.name || note.owner.email} />
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">
              {note.owner.name}{' '}
              <span className="font-normal text-muted italic">(Propriétaire)</span>
            </div>
            <div className="truncate text-muted">{note.owner.email}</div>
          </div>
        </div>
        {note.collaborators.map((person) => (
          <div key={person.id} className="flex items-center gap-3">
            <Avatar name={person.name || person.email} />
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{person.name}</div>
              <div className="truncate text-muted">{person.email}</div>
            </div>
            {(isOwner || person.id === me.id) && (
              <IconButton
                icon={X}
                label={person.id === me.id ? 'Quitter la note' : `Retirer ${person.name}`}
                onClick={() => {
                  remove.mutate(person.id);
                  if (person.id === me.id) onClose();
                }}
              />
            )}
          </div>
        ))}
        <form
          className="flex items-center gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-line text-muted">
            <UserPlus size={18} />
          </span>
          <input
            type="email"
            autoFocus
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              add.reset();
            }}
            placeholder="Adresse e-mail de la personne à ajouter"
            aria-label="Adresse e-mail du collaborateur"
            className="bare-input h-9"
          />
        </form>
        {add.error && <p className="text-xs text-danger">{errorMessage(add.error)}</p>}
      </div>
      <div className="flex justify-end gap-2 px-4 py-3">
        <button
          type="button"
          className="h-9 rounded px-6 text-sm font-medium hover:bg-hover"
          onClick={onClose}
        >
          Annuler
        </button>
        <button
          type="button"
          className="h-9 rounded px-6 text-sm font-medium hover:bg-hover"
          disabled={add.isPending}
          onClick={() => (email.trim() ? submit() : onClose())}
        >
          Enregistrer
        </button>
      </div>
    </Dialog>
  );
}
